// codiOS/components/PersonCard/PersonCard.js
import React from "react";
import {
  SparklesIcon,
  MapPinIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";

const fallbackImg = "/person.png";

const Chip = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-700">
    {children}
  </span>
);

const ScorePill = ({ score }) => {
  if (typeof score !== "number") return null;

  if (score === 0) {
    return (
      <span className="ml-auto inline-flex items-center gap-1 rounded-md bg-yellow-50 px-2 py-0.5 text-[11px] font-medium text-yellow-800 ring-1 ring-yellow-200">
        Incomplete profile
      </span>
    );
  }

  return (
    <span className="ml-auto inline-flex items-center gap-1 rounded-md bg-gradient-to-r from-fuchsia-100 via-sky-100 to-emerald-100 px-2 py-0.5 text-[11px] font-medium text-gray-700 ring-1 ring-black/5">
      Match {Math.round(score * 100)}%
    </span>
  );
};


const PersonCard = ({
  person = {},
  meta = {},             // { why: string[], score: number }
  className = "",
  onView,                // (person) => void
  onMessage,             // (person) => void
  onPress,
}) => {
  const {
    email,
    firstName,
    lastName,
    username,
    profileImage,
    builderProfile = {},
    city,
    country,
  } = person;

  const name =
    [firstName, lastName].filter(Boolean).join(" ") ||
    username ||
    email ||
    "Unknown Builder";

  const identity =
    Array.isArray(builderProfile.identity) && builderProfile.identity.length
      ? builderProfile.identity[0]
      : null;

  const location =
    builderProfile.location ||
    [city, country].filter(Boolean).join(", ") ||
    null;

  const why = Array.isArray(meta.why) ? meta.why : [];
  const score = typeof meta.score === "number" ? meta.score : null;

  return (
    <div
        className={`group relative rounded-2xl p-[1px] bg-gradient-to-tr from-fuchsia-200/60 via-sky-200/60 to-emerald-200/60 ${className}`}
        onClick={() => onPress?.(person)} 
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onPress?.(person);
        }}
    >
      <div className="h-full rounded-2xl bg-white p-4 shadow-sm ring-1 ring-black/5 transition-all group-hover:-translate-y-0.5 group-hover:shadow-md">
        {/* Header */}
        <div className="flex items-center gap-3">
          <img
            src={profileImage || fallbackImg}
            alt={name}
            className="h-12 w-12 rounded-xl object-cover ring-1 ring-black/5"
            onError={(e) => (e.currentTarget.src = fallbackImg)}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <div className="truncate font-semibold text-gray-900">{name}</div>
              <ScorePill score={score} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {identity && (
                <Chip>
                  <SparklesIcon className="h-3.5 w-3.5 mr-1" />
                  {identity}
                </Chip>
              )}
              {location && (
                <Chip>
                  <MapPinIcon className="h-3.5 w-3.5 mr-1" />
                  {location}
                </Chip>
              )}
            </div>
          </div>
        </div>

        {/* Why chips */}
        {why.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {why.slice(0, 4).map((w, i) => (
              <Chip key={i}>{w}</Chip>
            ))}
          </div>
        )}

        {/* Footer actions */}
        <div className="mt-4 flex items-center gap-2">
          {onView ? (
            <button
              onClick={() => onView(person)}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50"
            >
              View profile <ArrowRightIcon className="h-3.5 w-3.5" />
            </button>
          ) : username ? (
            <a
              href={`/${username}/profile`}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50"
            >
              View profile <ArrowRightIcon className="h-3.5 w-3.5" />
            </a>
          ) : null}

          {onMessage ? (
            <button
              onClick={() => onMessage(person)}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50"
            >
              <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
              Message
            </button>
          ) : email ? (
            <a
              href={`/messages/${[localStorage.getItem("userEmail"), email].filter(Boolean).sort().join("_")}`}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50"
            >
              <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
              Message
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PersonCard;
